import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime, linkifySegments } from "@/lib/helpers";
import { cn } from "@/lib/utils";
import { Bell } from "lucide-react";
import { toast } from "sonner";

const NotificationMessage = ({ text }) => (
  <p className="text-sm text-muted-foreground">
    {linkifySegments(text).map((seg) =>
      seg.type === "link" ? (
        <a
          key={seg.key}
          href={seg.href}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-[hsl(var(--role-accent))] underline underline-offset-2"
          data-testid="notification-link"
        >
          {seg.text}
        </a>
      ) : (
        <span key={seg.key}>{seg.text}</span>
      )
    )}
  </p>
);

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setNotifications(res.data);
    } catch {
      toast.error("Could not load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch (err) {
      console.error("Failed to mark notification as read", err);
    }
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Could not mark all as read");
    }
  };

  if (loading) return <Skeleton className="h-64 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Confirmations, reminders, and updates all in one place.</p>
        {notifications.some((n) => !n.read) && (
          <Button variant="outline" size="sm" onClick={markAllRead} data-testid="notifications-page-mark-all-read-button">
            Mark all read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="rounded-[var(--radius)] border bg-card p-12 text-center space-y-2" data-testid="notifications-empty-state">
          <Bell className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No notifications yet.</p>
        </div>
      ) : (
        <div className="rounded-[var(--radius)] border bg-card divide-y overflow-hidden">
          {notifications.map((n) => (
            <button
              key={n.id}
              onClick={() => markRead(n.id)}
              className={cn("w-full text-left p-4 sm:p-5 hover:bg-accent transition-colors", !n.read && "bg-[hsl(var(--role-accent))]/5")}
              data-testid="notification-list-item"
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="h-2 w-2 rounded-full bg-[hsl(var(--role-accent))] mt-1.5 shrink-0" />}
                <div className={cn("min-w-0", n.read && "pl-4")}>
                  <p className="text-sm font-medium">{n.title}</p>
                  <NotificationMessage text={n.message} />
                  <p className="text-xs text-muted-foreground mt-1">{formatDateTime(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
